import React, { useState } from 'react';
// components
import FormGrid from 'shared/grids/FormGrid';
import DataGrid from 'shared/grids/DataGrid';
import FormDatePicker from 'shared/formControls/FormDatePicker';
import { BaseCell, CheckboxCell, DateCell } from 'shared/grids/Cells';
import DocumentTypesSelectCell from '../DocumentTypesSelectCell';
import { FormInput } from 'shared/formControls';
import { DownloadLink, IconButton } from 'shared/buttons';
import { Field } from '@gulfwinds/ui-components';
// hooks
import { useToast } from 'shared/toast';
// helpers
import { getSourceConfig } from '../config';
// Fetch
import { toggleFilePin } from 'src/fetch/files';

const FileMainInfo = ({
  id, dataItem, onPreview, ...props
}) => {
  const {
    name, url, urlForPreview, fileType,
  } = dataItem;

  const lcFileType = fileType.toLowerCase();
  const hasPreview = urlForPreview && (lcFileType.includes('pdf') || lcFileType.includes('image'));

  const openPreview = () => onPreview(dataItem);
  return (
    <BaseCell {...props}>
      <div className='attachment-main-info-cell'>
        <div className='attachment-name' title={name}>{name}</div>
        <div className='attachment-actions'>
          {hasPreview && (
            <IconButton
              id={`${id}-preview-button`}
              icon='preview'
              onClick={openPreview}
              themeColor='secondary'
            />
          )}
          <DownloadLink id={`${id}-download-button`} url={url} />
        </div>
      </div>
    </BaseCell>
  );
};

const FilesTable = ({
  id,
  source,
  sourceId,
  needReload,
  didReload,
  handleReload,
  onPreview,
  disabled,
  readOnly,
  showDriverDoc,
  afterSave,
  supportsDqf,
  ...props
}) => {
  const config = getSourceConfig(source);

  const { getFiles, deleteFile, putFile } = config;

  // Disable sorting for DQF
  const [allowSorting, setAllowSorting] = useState(true);

  const getData = (params) => getFiles(sourceId, params)
    .then((data) => {
      if (supportsDqf) {
        setAllowSorting(!params.dqfOnly);
      }
      return data;
    });

  const toast = useToast();

  const err = ' method is not yet supported supported. A put configuration is needed in gulfwinds/src/GulfWinds.CMS/ClientApp/src/shared/components/Attachments/config.js';
  const onDelete = ({ id }, onSuccess, onError, onFinish) => {
    if (!deleteFile) {
      console.error(`Delete ${err}`);
      return onError(`Delete ${err}`);
    }
    return deleteFile(sourceId, id)
      .then(() => {
        onSuccess();
        if (afterSave) {
          afterSave();
        }
      })
      .catch(onError)
      .finally(onFinish);
  };

  const onSave = ({
    description, date, id, ...dataItem
  }, onSuccess, onError, onFinish) => {
    if (!putFile) {
      console.error(`Put ${err}`);
      return onError(`Put ${err}`);
    }
    return putFile(sourceId, id, {
      ...dataItem,
      description,
      date,
      id,
    })
      .then(() => {
        onSuccess();
        if (afterSave) {
          afterSave();
        }
      })
      .catch(onError)
      .finally(onFinish);
  };

  const onPin = (dataItem) => {
    const { id } = dataItem;
    toggleFilePin(id)
      .then(() => {
        handleReload();
      })
      .catch((error) => {
        toast.error({ message: 'Failed to toggle file pin', error });
      });
  };

  const DriverDocCell = ({ dataItem }) => (
    <BaseCell>
      {dataItem.isInEdit ? (
        <Field component={CheckboxCell} checked={dataItem.driverDoc} name='driverDoc' />
      ) : (
        <CheckboxCell checked={dataItem.driverDoc} disabled />
      )}
    </BaseCell>
  );

  function getColumns() {
    const driverDocColumn = {
      title: 'Driver Doc',
      field: 'driverDoc',
      editor: 'boolean',
      cell: DriverDocCell,
    };

    const defaultColumns = [
      {
        title: 'File',
        field: 'name',
        cell: (props) => <FileMainInfo onPreview={onPreview} {...props} id={`${id}-${props.dataItem.id}`} />,
        editable: false,
        width: 225,
      },
      {
        title: 'Document Type',
        field: 'documentTypeId',
        width: 175,
        cell: (props) => <DocumentTypesSelectCell {...props} isField />,
      },
      {
        title: 'Document Type Name',
        field: 'documentType',
        width: 0,
        cell: null,
      },
      {
        title: 'Description',
        field: 'description',
        width: 300,
        cell: ({ dataItem, ...props }) => (
          <BaseCell {...props}>
            {dataItem.isInEdit ? <Field name='description' component={FormInput} /> : dataItem.description}
          </BaseCell>
        ),
      },
      ...(showDriverDoc ? [driverDocColumn] : []),
      {
        title: 'Uploaded By',
        field: 'createdBy',
        editable: false,
      },
      {
        title: 'Uploaded On',
        field: 'createdDateTime',
        editable: false,
        isDateTime: true,
        width: 150,
      },
      {
        title: 'Pinned',
        field: 'isPinned',
        width: 100,
        cell: ({ dataItem, ...props }) => (
          <BaseCell {...props}>
            <IconButton
              id={`${id}-${dataItem.id}-pin-button`}
              icon={dataItem.isPinned ? 'unpin' : 'pin'}
              onClick={() => onPin(dataItem)}
              disabled={readOnly || disabled || dataItem.isSubmitting}
              tooltip={dataItem.isPinned ? 'Unpin' : 'Pin'}
            />
          </BaseCell>
        ),
      },
    ];

    return defaultColumns;
  }

  const getExportColumns = () => getColumns()
    .flatMap((col) => (col.field === 'documentTypeId' ? [] : col));

  const requestExport = (params) => getData(params).then(({ data }) => data.map((d) => ({ ...d, date: d.date?.split?.('T')?.[0] })));

  return (
    <DataGrid
      name='Files'
      id={id}
      itemName="Files"
      tableMaint
      hideAddButton
      gridComponent={FormGrid}
      getData={getData}
      actionCellWidth={100}
      onRowDelete={onDelete}
      onRowSubmit={onSave}
      forceBgReload={needReload}
      didLoad={didReload}
      hideActionsCell={readOnly || !(putFile || deleteFile)}
      unifiedTextSearchHint='File Name, Description, Document Type'
      editField='isInEdit'
      dataItemKey='id'
      columns={getColumns()}
      exportColumns={getExportColumns()}
      requestExport={requestExport}
      {...props}
      sortable={allowSorting}
      isMultiSorting
    />
  );
};

export default FilesTable;